import { tasksApi, knowledgeApi, KnowledgeItem } from './api';
import { TaskResultResponse } from '../types/generation';

export interface TaskResultInfo {
  documentId: string;
  documentExists: boolean;
  taskId: string;
  taskType: string;
  errorMessage?: string;
}

/**
 * Сервис для надежной обработки результатов задач
 * Обеспечивает стабильную навигацию к результатам и проверку существования документов
 */
export class TaskResultService {
  private static cache = new Map<string, TaskResultInfo>();
  private static readonly CACHE_TTL = 30000; // 30 секунд

  /**
   * Получает информацию о результате задачи с кэшированием
   * Сначала пытается использовать knowledgeDocumentId из данных задачи, если доступен
   */
  static async getTaskResultInfo(taskId: string, knowledgeDocumentId?: string): Promise<TaskResultInfo | null> {
    try {
      // Проверяем кэш
      const cached = this.cache.get(taskId);
      if (cached) {
        return cached;
      }

      // Если у нас есть knowledgeDocumentId, используем его напрямую
      if (knowledgeDocumentId) {
        console.log(`[TaskResultService] Используем knowledgeDocumentId из данных задачи: ${knowledgeDocumentId}`);
        
        // Проверяем существование документа
        let documentExists = false;
        try {
          const document = await knowledgeApi.getFile(knowledgeDocumentId);
          if (document && document.id) {
            documentExists = true;
          }
        } catch (error) {
          documentExists = false;
        }

        const resultInfo: TaskResultInfo = {
          documentId: knowledgeDocumentId,
          documentExists,
          taskId,
          taskType: 'unknown' // Тип будет уточнен позже если нужно
        };
        
        // Кэшируем результат
        this.cache.set(taskId, resultInfo);
        setTimeout(() => {
          this.cache.delete(taskId);
        }, this.CACHE_TTL);
        
        return resultInfo;
      }

      // Если knowledgeDocumentId нет, делаем HTTP запрос
      console.log(`[TaskResultService] knowledgeDocumentId не найден, делаем HTTP запрос для задачи: ${taskId}`);
      const taskResult = await tasksApi.getTaskResult(taskId);
      const resultInfo = await this.processTaskResult(taskId, taskResult.data);
      
      // Кэшируем результат
      this.cache.set(taskId, resultInfo);
      
      // Очищаем кэш через TTL
      setTimeout(() => {
        this.cache.delete(taskId);
      }, this.CACHE_TTL);

      return resultInfo;
    } catch (error) {
      console.error(`[TaskResultService] Ошибка при получении результата задачи ${taskId}:`, error);
      return null;
    }
  }

  /**
   * Обрабатывает результат задачи и определяет ID документа
   */
  private static async processTaskResult(taskId: string, taskResult: TaskResultResponse): Promise<TaskResultInfo> {
    // Ищем knowledgeDocumentId в разных возможных местах
    let documentId = null;
    
    // Проверяем в основном объекте результата
    if (taskResult.knowledgeDocumentId) {
      documentId = taskResult.knowledgeDocumentId;
    }
    // Проверяем в data
    else if (taskResult.data?.knowledgeDocumentId) {
      documentId = taskResult.data.knowledgeDocumentId;
    }
    // Проверяем documentId в data
    else if (taskResult.data?.documentId) {
      documentId = taskResult.data.documentId;
    }
    // Проверяем id в data
    else if (taskResult.data?.id) {
      documentId = taskResult.data.id;
    }

    // Если documentId не найден, пытаемся найти документ по taskId в существующей структуре
    if (!documentId) {
      // Получаем структуру базы знаний
      const cachedItems = knowledgeApi.getCachedItems();
      const items = cachedItems || await knowledgeApi.getItems();
      
      // Ищем документ с именем содержащим тип задачи или ID
      const taskName = taskResult.name || taskResult.type || 'task';
      const foundDocument = this.findDocumentInStructure(items, taskId, taskName);
      
      if (foundDocument) {
        documentId = foundDocument.id;
      } else {
        // В крайнем случае используем taskId
        documentId = taskId;
      }
    }

    // Проверяем существование документа
    let documentExists = false;
    try {
      const document = await knowledgeApi.getFile(documentId);
      if (document && document.id) {
        documentExists = true;
      }
    } catch (error) {
      documentExists = false;
    }

    return {
      documentId: documentId || taskId,
      documentExists,
      taskId,
      taskType: taskResult.type || 'unknown',
      errorMessage: taskResult.error || undefined
    };
  }

  /**
   * Поиск документа в структуре базы знаний по различным критериям
   */
  private static findDocumentInStructure(items: any[], taskId: string, taskName: string): any | null {
    if (!items || !Array.isArray(items)) return null;

    for (const item of items) {
      // Ищем файл (не папку)
      if (item.itemType === 'file') {
        // Проверяем по ID
        if (item.id === taskId) {
          return item;
        }
        
        // Проверяем по имени (содержит тип задачи)
        if (item.name && taskName && item.name.toLowerCase().includes(taskName.toLowerCase())) {
          return item;
        }
      }
      
      // Рекурсивно ищем в дочерних элементах
      if (item.children && Array.isArray(item.children)) {
        const found = this.findDocumentInStructure(item.children, taskId, taskName);
        if (found) return found;
      }
    }

    return null;
  }

  /**
   * Выполняет навигацию к результату задачи
   * @deprecated Используйте прямую навигацию по knowledgeDocumentId
   */
  static async navigateToResult(
    taskId: string, 
    navigate: (path: string) => void,
    knowledgeDocumentId?: string
  ): Promise<boolean> {
    // Простая навигация без HTTP запросов
    if (knowledgeDocumentId) {
      navigate(`/knowledge/${knowledgeDocumentId}`);
      return true;
    } else {
      navigate('/knowledge');
      return true;
    }
  }

  /**
   * Предзагружает и проверяет структуру базы знаний
   */
  static async preloadKnowledgeStructure(): Promise<void> {
    try {
      // Сначала проверяем кэш
      const cachedItems = knowledgeApi.getCachedItems();
      if (cachedItems && cachedItems.length > 0) {
        return;
      }
      
      // Если кэш пуст, загружаем с сервера
      await knowledgeApi.getItems();
    } catch (error) {
      // Тихо игнорируем ошибки предзагрузки
    }
  }

  /**
   * Очищает кэш
   */
  static clearCache(): void {
    this.cache.clear();
  }
} 